import { useEffect, useMemo, useRef } from 'react';

import { Typography, Paper, Divider } from '@mui/material';
import { Box } from '@mui/system';
import { EventItem } from '@scorenco/components';
import { Types, useTranslation } from '@scorenco/core';
import { animate } from 'framer-motion';

import { T_KEYS } from '@/translations';

type OtherResultsProps = {
  events: Types.Competitions_Event_Detail[];
};

export const OtherResults = ({ events }: OtherResultsProps) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const nbTeams = events.length >= 20 ? 20 : events.length;

  const shuffleEvents = (items: Types.Competitions_Event_Detail[]) =>
    [...items].sort(() => Math.random() - 0.5);

  const shuffledEvents = useMemo(() => shuffleEvents(events), [events]);
  const visibleEvents = shuffledEvents.slice(0, nbTeams);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const pauseDuration = 1;
    const scrollDuration = 6;
    let direction: 'down' | 'up' = 'down';
    let controls: ReturnType<typeof animate>;
    let startTimeout: ReturnType<typeof setTimeout>;

    const loop = () => {
      const distance = container.scrollHeight - container.clientHeight;
      if (distance <= 0) {
        return;
      }

      const from = container.scrollTop;
      const to = direction === 'down' ? distance : 0;

      controls = animate(from, to, {
        duration: scrollDuration,
        ease: 'linear',
        onUpdate: (v) => (container.scrollTop = v),
        onComplete: () => {
          direction = direction === 'down' ? 'up' : 'down';
          startTimeout = setTimeout(loop, pauseDuration * 1000);
        },
      });
    };

    startTimeout = setTimeout(loop, pauseDuration * 1000);

    return () => {
      clearTimeout(startTimeout);
      controls?.stop();
    };
  }, [visibleEvents.length]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography
        variant="h3"
        sx={{
          textTransform: 'uppercase',
          m: 0,
          mb: 1,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {t(T_KEYS.OTHER_ROUND_EVENTS)}
      </Typography>
      <Paper
        variant="bordered"
        ref={scrollRef}
        sx={{
          minHeight: 0,
          overflow: 'hidden',
          padding: '0.7rem 0',
        }}
      >
        {visibleEvents.map((event, idx) => (
          <Box
            key={idx}
            sx={{
              width: 'calc(100% + 4rem)',
              padding: '0 2rem',
              boxSizing: 'border-box',
            }}
          >
            {idx !== 0 && (
              <Divider
                sx={{
                  width: 'calc(100% + 4rem)',
                  transform: 'translateX(-2rem)',
                  marginBottom: '1rem',
                  opacity: '0.6',
                }}
              />
            )}
            <EventItem event={event} showCompetition={false} showDate={true} />
          </Box>
        ))}
      </Paper>
    </Box>
  );
};
